class CartDiscount extends HTMLElement {
  constructor() {
    super();
    
    this.form = this.querySelector('.cart__discount-form');
    this.input = this.querySelector('input[name="discount"]');
    this.errorContainer = this.querySelector('.cart__discount-error');
    this.errorText = this.querySelector('.cart__discount-error-text');
    
    this.handleRemoveClick = this.handleRemoveClick.bind(this);
  }

  connectedCallback() {
    this.init();
    
    // Ensure component is visible in Theme Editor
    if (window.Shopify?.designMode) {
      this.style.display = '';
    }
    
    // Only run these if we're in a real store, not Theme Editor
    if (this.isRealStore()) {
      this.hideRemovedDiscounts();
      this.updateAllDiscountDisplays();
    }
  }

  // Check if we're in a real store vs Theme Editor
  isRealStore() {
    // Theme Editor sets Shopify.designMode to true
    // In Theme Editor, show the component but don't run cart API calls
    return window.Shopify && 
           window.Shopify.routes && 
           !window.Shopify.designMode;
  }

  init() {
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.addEventListener('click', this.handleRemoveClick);
  }

  getRemovedDiscounts() {
    try {
      const removed = localStorage.getItem('removedDiscounts');
      return removed ? JSON.parse(removed) : [];
    } catch (e) {
      return [];
    }
  }

  addToRemovedList(code) {
    const removed = this.getRemovedDiscounts();
    if (!removed.includes(code.toUpperCase())) {
      removed.push(code.toUpperCase());
      localStorage.setItem('removedDiscounts', JSON.stringify(removed));
    }
  }

  removeFromRemovedList(code) {
    const removed = this.getRemovedDiscounts();
    const filtered = removed.filter(c => c !== code.toUpperCase());
    localStorage.setItem('removedDiscounts', JSON.stringify(filtered));
  }

  clearRemovedList() {
    localStorage.removeItem('removedDiscounts');
  }

  hideRemovedDiscounts() {
    const removed = this.getRemovedDiscounts();
    
    if (removed.length === 0) return;

    const discountItems = this.querySelectorAll('.cart__discount-item');

    discountItems.forEach(item => {
      const codeElement = item.querySelector('.cart__discount-code');
      const codeText = codeElement?.textContent.trim().toUpperCase();
      
      if (codeText && removed.includes(codeText)) {
        // Only hide the individual discount pill
        item.style.display = 'none';
      }
    });
  }

  async updateAllDiscountDisplays() {
    const removed = this.getRemovedDiscounts();
    
    if (removed.length === 0) return;

    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      await this.updateLineItemPrices(cart, removed);
      await this.updateOrderTotalSection(cart, removed);

    } catch (error) {
      console.error('Error updating discount displays:', error);
    }
  }

  async updateLineItemPrices(cart, removed) {
    const cartItems = document.querySelectorAll('cart-drawer-items .cart-item, .cart-items .cart-item');
    
    cartItems.forEach((itemElement, index) => {
      const cartItem = cart.items[index];
      if (!cartItem) return;

      const hasRemovedDiscount = cartItem.line_level_discount_allocations?.some(allocation => {
        const discountCode = allocation.discount_application?.title?.toUpperCase();
        return discountCode && removed.includes(discountCode);
      });

      if (hasRemovedDiscount) {
        const discountBadges = itemElement.querySelectorAll('[class*="discount"], [class*="sale"]');
        discountBadges.forEach(badge => {
          if (badge.textContent && removed.some(code => badge.textContent.toUpperCase().includes(code))) {
            badge.style.display = 'none';
          }
        });

        const finalPriceElement = itemElement.querySelector('.cart-item__final-price, .price__sale .price-item--sale');
        const regularPriceElement = itemElement.querySelector('.cart-item__old-price, .price__regular .price-item--regular');

        if (finalPriceElement && regularPriceElement) {
          finalPriceElement.style.display = 'none';
          regularPriceElement.style.textDecoration = 'none';
          regularPriceElement.style.color = 'inherit';
          regularPriceElement.style.fontSize = 'inherit';
        }

        const totalPriceElement = itemElement.querySelector('[data-cart-item-price], .cart-item__totals');
        if (totalPriceElement && cartItem.original_line_price) {
          const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: cart.currency || 'USD',
            minimumFractionDigits: 2
          });
          
          const originalPrice = formatter.format(cartItem.original_line_price / 100);
          const priceTextElement = totalPriceElement.querySelector('.price, [class*="price"]');
          if (priceTextElement) {
            priceTextElement.textContent = originalPrice;
          }
        }
      }
    });
  }

  async updateOrderTotalSection(cart, removed) {
    const orderTotalSection = document.querySelector('.cart__footer, .drawer__footer, .totals, [class*="cart-drawer__footer"]');
    
    if (!orderTotalSection) return;

    // Hide discount badges in totals section (be specific to avoid hiding the entire discount input component)
    const discountBadges = orderTotalSection.querySelectorAll('.discounts__discount, [data-discount-code]:not(cart-discount):not(cart-discount *)');
    
    discountBadges.forEach(badge => {
      const badgeText = badge.textContent?.trim().toUpperCase();
      if (badgeText && removed.some(code => badgeText.includes(code))) {
        badge.style.display = 'none';
        
        const parentLi = badge.closest('li');
        if (parentLi) {
          parentLi.style.display = 'none';
        }
      }
    });

    // Recalculate total
    let adjustedTotal = cart.total_price;

    cart.cart_level_discount_applications?.forEach(discount => {
      const discountCode = discount.title?.toUpperCase();
      if (discountCode && removed.includes(discountCode)) {
        adjustedTotal += discount.total_allocated_amount || 0;
      }
    });

    cart.items.forEach(item => {
      item.line_level_discount_allocations?.forEach(allocation => {
        const discountCode = allocation.discount_application?.title?.toUpperCase();
        if (discountCode && removed.includes(discountCode)) {
          adjustedTotal += allocation.amount;
        }
      });
    });

    // Update displayed total
    const totalElements = document.querySelectorAll(
      '[data-cart-total], .cart-drawer__footer .totals__total-value, #CartDrawer .totals__total-value, .totals__total-value, .cart__ctas__total-price'
    );
    
    if (totalElements.length > 0) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cart.currency || 'USD',
        minimumFractionDigits: 2
      });
      
      const newTotal = formatter.format(adjustedTotal / 100);
      
      totalElements.forEach(element => {
        element.textContent = newTotal;
      });
    }
  }

  handleRemoveClick(e) {
    const removeButton = e.target.closest('.cart__discount-remove');
    
    if (removeButton) {
      e.preventDefault();
      e.stopPropagation();
      
      const discountCode = removeButton.dataset.discountCode || removeButton.getAttribute('data-discount-code');
      
      if (discountCode) {
        this.removeDiscount(discountCode);
      }
    }
  }

  onSubmit(event) {
    event.preventDefault();
    
    const discountCode = this.input.value.trim();
    if (!discountCode) return;

    this.applyDiscount(discountCode);
  }

  async applyDiscount(discountCode) {
    this.hideError();
    this.input.disabled = true;

    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();
      
      const existingCodes = cart.discount_codes || [];
      const removed = this.getRemovedDiscounts();
      
      const alreadyAppliedAndVisible = existingCodes.some(d => {
        const code = (typeof d === 'string' ? d : d.code).toUpperCase();
        return code === discountCode.toUpperCase() && !removed.includes(code);
      });
      
      if (alreadyAppliedAndVisible) {
        this.showError(window.cartStrings?.discountAlreadyApplied || 'This discount code is already applied.');
        this.input.disabled = false;
        return;
      }

      const isValid = await this.validateDiscountCode(discountCode);
      
      if (!isValid) {
        this.showError(window.cartStrings?.discountNotApplicable || 'This discount code is not valid or cannot be applied to your cart.');
        this.input.disabled = false;
        this.input.value = '';
        return;
      }

      // Remove from removed list if re-applying
      this.removeFromRemovedList(discountCode);

      // REVERT TO ORIGINAL: Store in cart.attributes (no page reload!)
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            discount: discountCode
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Refresh cart drawer without reload
      await this.refreshCart();
      this.input.value = '';
      this.input.disabled = false;

    } catch (error) {
      console.error('Error applying discount:', error);
      this.showError(window.cartStrings?.discountError || 'There was an error applying the discount code.');
      this.input.disabled = false;
    }
  }

  async validateDiscountCode(discountCode) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}discount/${discountCode}`, {
        method: 'HEAD',
        redirect: 'manual'
      });
      
      return response.status === 303 || response.status === 302 || response.type === 'opaqueredirect';
    } catch (error) {
      console.error('Error validating discount code:', error);
      return true;
    }
  }

  async refreshCart() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      const newFooter = html.querySelector('.drawer__footer');
      const currentFooter = document.querySelector('.drawer__footer');
      
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }

      const newCartDrawerItems = html.querySelector('cart-drawer-items');
      const currentCartDrawerItems = document.querySelector('cart-drawer-items');
      
      if (newCartDrawerItems && currentCartDrawerItems) {
        currentCartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
      }

      const newBubble = html.querySelector('.cart-count-bubble');
      const currentBubble = document.querySelector('.cart-count-bubble');
      
      if (newBubble && currentBubble) {
        currentBubble.textContent = newBubble.textContent;
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: cart });
      }

    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  async removeDiscount(discountCode) {
    this.addToRemovedList(discountCode);

    const discountPill = this.querySelector(`.cart__discount-remove[data-discount-code="${discountCode}"]`)?.closest('.cart__discount-item');
    
    if (discountPill) {
      discountPill.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      discountPill.style.opacity = '0';
      discountPill.style.transform = 'scale(0.95)';
      
      setTimeout(() => {
        // Only hide the individual discount pill, not the entire box
        discountPill.style.display = 'none';
      }, 200);
    }

    await this.updateAllDiscountDisplays();
  }

  showError(message) {
    this.errorText.textContent = message;
    this.errorContainer.classList.remove('hidden');
  }

  hideError() {
    this.errorContainer.classList.add('hidden');
    this.errorText.textContent = '';
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleRemoveClick);
  }
}

customElements.define('cart-discount', CartDiscount);

// Checkout handler
document.addEventListener('DOMContentLoaded', function() {
  const checkoutButtons = document.querySelectorAll('#CartDrawer-Checkout, .cart__checkout-button, [name="checkout"]');
  
  checkoutButtons.forEach(button => {
    button.addEventListener('click', async function(event) {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();
        
        const discountComponent = document.querySelector('cart-discount');
        
        if (cart.discount_codes && cart.discount_codes.length > 0) {
          const codes = cart.discount_codes.map(d => typeof d === 'string' ? d : d.code);
          const removed = discountComponent ? discountComponent.getRemovedDiscounts() : [];
          const activeDiscounts = codes.filter(code => !removed.includes(code.toUpperCase()));
          
          event.preventDefault();
          
          if (activeDiscounts.length > 0) {
            const discountParam = activeDiscounts.join(',');
            const checkoutUrl = `${window.Shopify.routes.root}checkout?discount=${encodeURIComponent(discountParam)}`;
            
            if (discountComponent) {
              discountComponent.clearRemovedList();
            }
            
            window.location.href = checkoutUrl;
          } else {
            if (discountComponent) {
              discountComponent.clearRemovedList();
            }
            window.location.href = `${window.Shopify.routes.root}checkout`;
          }
        }
      } catch (error) {
        console.error('Error applying discount at checkout:', error);
      }
    });
  });
});